import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { createPortaria, listPortaria } from './portariaApi'

type FacialDevice = { id: string; name: string; manufacturer: string; model: string; active: boolean; connectionStatus: string }

export function FacialDevicesPanel({ clientId, canManage }: {
  clientId: string; canManage: boolean
}) {
  const [devices, setDevices] = useState<FacialDevice[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const load = useCallback(async () => {
    const result = await listPortaria<FacialDevice>(clientId, 'facial-devices')
    setDevices(result)
  }, [clientId])

  useEffect(() => {
    if (!canManage) return
    const refresh = () => void load().catch(() => setError('Não foi possível carregar os controladores.'))
    refresh()
    const timer = window.setInterval(refresh, 10000)
    return () => window.clearInterval(timer)
  }, [canManage, load])

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const values = new FormData(form)
    setBusy(true); setError(''); setMessage('')
    try {
      await createPortaria(clientId, 'facial-devices', {
        name: String(values.get('name') ?? '').trim(),
        manufacturer: String(values.get('manufacturer') ?? '').trim(),
        model: String(values.get('model') ?? '').trim(),
      })
      await load(); form.reset(); setMessage('Controlador cadastrado desativado.')
    } catch { setError('Não foi possível cadastrar o controlador. Confira os dados e o nome já usado.') }
    finally { setBusy(false) }
  }

  return <>
    <div className="portaria-section-title"><h2>Controladores faciais</h2>
      <p>Registre fabricante e modelo para preparar a integração. O controlador permanece desativado; nenhum rosto ou credencial é enviado.</p>
    </div>
    {!canManage ? <div className="portaria-card">Somente a administração pode consultar controladores.</div> : <>
      {error && <div className="portaria-error" role="alert">{error}</div>}
      {message && <div className="portaria-success" role="status">{message}</div>}
      <form className="portaria-card portaria-form-grid" onSubmit={event => void create(event)}>
        <label>Nome do controlador<input name="name" required maxLength={120} /></label>
        <label>Fabricante<input name="manufacturer" required maxLength={120} /></label>
        <label>Modelo<input name="model" required maxLength={120} /></label>
        <button disabled={busy}>Cadastrar desativado</button>
      </form>
      <div className="portaria-card portaria-list"><h3>Controladores cadastrados</h3>
        {devices.length ? devices.map(device => <div key={device.id}>
          <strong>{device.name}</strong><small>{device.manufacturer} · {device.model} · {!device.active ? 'Desativado' : device.connectionStatus === 'online' ? '🟢 Ativo' : '🔴 Offline'}</small>
        </div>) : <p>Nenhum controlador cadastrado.</p>}
      </div>
    </>}
  </>
}
